// SCR-CSH-01 Capital (frozen mockups-csh-v1, ADR-087, ADR-095). Owner only. The summary card and the quick actions
// concern the whole page; tabs: By place · History. Truck round cash joins "By place" in P5.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  List,
  ListRow,
  Notice,
  QuickActions,
  SkeletonList,
  SummaryCard,
  Tabs,
  type QuickAction,
} from '../../design/kit';
import { CASH, loadBusinessCash, loadCapitalHistory, loadCashPlaces, type CapitalMove } from '../../data/cash';
import { Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate, formatMoney } from '../../lib/format';
import { useTab } from '../catalog/common';
import { BankSheet, ContributionSheet, OpeningCashSheet, WithdrawalSheet } from './CashSheets';

const TABS = ['places', 'caphistory'] as const;
type Tab = (typeof TABS)[number];
type Sheet = 'contribution' | 'withdrawal' | 'bank' | 'opening' | null;
const PLACE_ICON = { garage: 'layers', bank: 'cash', round: 'truck' } as const;
const MOVE_ICON = { contribution: 'plus', withdrawal: 'cash', bank_transfer: 'swap', opening: 'layers' } as const;

function moveTitle(m: CapitalMove): string {
  if (m.kind === 'bank_transfer') return m.direction === 'to_garage' ? 'h.bank.out' : 'h.bank.in';
  return `h.${m.kind}`;
}

export function CapitalPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const fmt = (v: string | number) => formatMoney(v, lang);
  const [tab, setTab] = useTab(TABS, 'places');
  const [sheet, setSheet] = useState<Sheet>(null);
  const cash = useQuery({ queryKey: [...CASH, 'business'], queryFn: loadBusinessCash });
  const places = useQuery({ queryKey: [...CASH, 'places'], queryFn: loadCashPlaces, enabled: tab === 'places' });
  const history = useQuery({ queryKey: [...CASH, 'history'], queryFn: loadCapitalHistory });
  const close = () => setSheet(null);

  if (cash.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  const c = cash.data;
  const actions: QuickAction[] = c
    ? [
        {
          id: 'contribution',
          label: t('act.contribution'),
          icon: 'plus',
          main: true,
          onSelect: () => setSheet('contribution'),
        },
        { id: 'withdrawal', label: t('act.withdrawal'), icon: 'cash', onSelect: () => setSheet('withdrawal') },
        { id: 'bank', label: t('act.bank'), icon: 'swap', onSelect: () => setSheet('bank') },
        ...(c.opening_open
          ? [
              {
                id: 'opening',
                label: t('act.openingcash'),
                icon: 'layers' as const,
                onSelect: () => setSheet('opening'),
              },
            ]
          : []),
      ]
    : [];
  const empty = history.data?.length === 0;

  return (
    <>
      {c ? (
        <SummaryCard
          label={t('cash.business')}
          value={fmt(c.business_cash)}
          grid
          figures={[
            { label: t('cash.garage'), value: fmt(c.garage) },
            { label: t('cash.bank'), value: fmt(c.bank) },
            { label: t('cap.net'), value: fmt(c.net_capital) },
            { label: t('cap.contrib'), value: fmt(c.total_contributions) },
          ]}
        />
      ) : (
        <SkeletonList rows={1} label={t('loading')} />
      )}
      <QuickActions actions={actions} />
      <Tabs
        label={t('p.capital')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {empty ? (
        <EmptyState icon="cash" title={t('cap.empty')} text={t('cap.empty.text')} />
      ) : tab === 'places' ? (
        !places.data ? (
          <SkeletonList label={t('loading')} />
        ) : (
          <>
            <List label={t('tab.places')}>
              {places.data.map((p) => (
                <ListRow
                  key={p.account_id}
                  testId={`place-${p.kind}`}
                  lead={{ icon: PLACE_ICON[p.kind] }}
                  title={p.kind === 'round' ? <Name>{p.name}</Name> : t(`cash.${p.kind}`)}
                  value={fmt(p.balance)}
                />
              ))}
            </List>
            <p className="muted">{t('cash.trucks.later')}</p>
          </>
        )
      ) : !history.data ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <List label={t('tab.caphistory')}>
          {history.data.map((m) => (
            <ListRow
              key={m.id}
              testId={`capmove-${m.id}`}
              lead={{ icon: MOVE_ICON[m.kind] }}
              title={t(moveTitle(m))}
              sub={
                <>
                  {formatDate(m.business_date, lang)}
                  {m.party && (
                    <>
                      {' · '}
                      <Name>{m.party}</Name>
                    </>
                  )}
                  {m.reason && (
                    <>
                      {' · '}
                      <span dir="auto">{m.reason}</span>
                    </>
                  )}
                  {m.reference && (
                    <>
                      {' · '}
                      <bdi className="yv-nowrap">{m.reference}</bdi>
                    </>
                  )}
                  {m.note && (
                    <>
                      {' · '}
                      <span dir="auto">{m.note}</span>
                    </>
                  )}
                </>
              }
              value={fmt(m.amount)}
            />
          ))}
        </List>
      )}
      {c && sheet === 'contribution' && <ContributionSheet cash={c} onClose={close} />}
      {c && sheet === 'withdrawal' && <WithdrawalSheet cash={c} onClose={close} />}
      {c && sheet === 'bank' && <BankSheet cash={c} onClose={close} />}
      {sheet === 'opening' && <OpeningCashSheet onClose={close} />}
    </>
  );
}
